// Shared rendering helpers for the service tabs (Wealth / Tax / Insurance).
// Each tab passes its own `renderDetail` to control which fields the
// per-row chips show; everything else (status badge, person name,
// container styling, empty/placeholder shapes) is shared.

import { formatCurrency } from '../../../utils/formatting';

export function EngagementsList({ engagements, peopleById, renderDetail }) {
  // Active first, then by name. Matches the API ordering but defensive
  // in case engagements get re-sorted upstream.
  const sorted = [...engagements].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'Active' ? -1 : 1;
    return (a.name || '').localeCompare(b.name || '');
  });

  return (
    <div className="space-y-1.5">
      {sorted.map(e => {
        const person = e.person_id ? peopleById?.[e.person_id] : null;
        return (
          <div
            key={e.id}
            className="flex items-center justify-between gap-4 bg-bone/30 rounded-[4px] px-3.5 py-3 hover:bg-bone/50 transition-colors"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-[2px] ${
                  e.status === 'Active' ? 'bg-deep-teal/10 text-deep-teal' : 'bg-rose/10 text-rose'
                }`}>
                  {e.status || 'Unknown'}
                </span>
                <span className="text-[13px] font-bold text-navy">{person?.name || e.name}</span>
              </div>
              <div className="flex items-center gap-4 mt-1.5 flex-wrap">
                {renderDetail(e)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Generic empty state for service tabs with no engagements yet.
export function EmptyState({ label }) {
  return (
    <div className="text-[12px] text-[#888] italic py-3 text-center">
      {label}
    </div>
  );
}

// Dashed-border placeholder card. Used for future-integration teasers
// (SEI, TaxDome, ProConnect) so users see what's coming.
export function PlaceholderCard({ title, body }) {
  return (
    <div className="bg-bone/40 border border-dashed border-gray-300 rounded-[4px] p-4">
      <div className="text-[12px] font-bold text-navy mb-1">{title}</div>
      <div className="text-[11px] text-[#666] leading-relaxed">{body}</div>
    </div>
  );
}

// "Add service" call-to-action. Currently displays an info-only message
// — the legacy modal had a working add flow tied to BWMClients.jsx.
// Phase C.1 wires this up to a real form on the new page; for now it's
// a visible reminder of what's coming, with a link back to the legacy
// list view where the add form still works.
export function AddServiceCTA({ division, label }) {
  const colors = {
    bwm: 'bg-bullogic-blue text-white hover:bg-bullogic-blue/90',
    bts: 'bg-deep-teal text-white hover:bg-deep-teal/90',
    bia: 'bg-sky-blue text-white hover:bg-sky-blue/90',
  };
  return (
    <button
      onClick={() => alert('Coming in Phase C.1 — use the Households list view to add a service for now.')}
      className={`text-[11px] font-bold px-3 py-1.5 rounded-[4px] transition-colors ${colors[division] || colors.bwm}`}
      title="Coming in Phase C.1"
    >
      {label}
    </button>
  );
}

// Convenience export — most tabs need formatCurrency too.
export { formatCurrency };
